/**
 * English, German and French articles.
 *
 * WHY THESE THREE TOPICS AND NOT OTHERS
 * Search Console (180 days) says three Arabic pages carry this site:
 *   /blog/al-istikhara-qabl-al-zawaj      54,067 impressions
 *   /blog/mada-bayn-al-khotba-wa-al-zawaj 17,857 impressions
 *   /blog/alamat-al-zawj-al-salih          4,797 impressions
 * All three are universal Islamic questions, not Egypt-specific ones — they
 * are asked in every language a Muslim searches in. So rather than invent
 * topics for the German and French markets, each of these is written properly
 * in each language. That also makes them TRUE translations, which is what lets
 * us declare real hreflang clusters instead of bare self-references.
 *
 * REGISTER
 * German uses "du", matching the two German articles already published — the
 * same split Arabic already has, where articles are conversational (Egyptian
 * dialect) and the product UI is formal. French uses "vous": the topic reaches
 * walis, mothers and fathers, and "tu" would read as too familiar to them.
 *
 * Every article carries the full du'a text where that is the search intent.
 * Somebody typing "dua istikhara for marriage" wants the words, immediately,
 * not three paragraphs of preamble.
 */

export const articlesI18n = [
  // ─────────────────────────────────────────────────────────────────────
  // ENGLISH
  // ─────────────────────────────────────────────────────────────────────
  {
    slug: 'istikhara-dua',
    lang: 'en',
    title: 'Istikhara Dua: The Full Text, Transliteration and Meaning',
    description:
      'The full istikhara dua in transliteration and English, what each line asks for, how to pray it, how many times to repeat it, and the three real signs of an answer. No sign-up needed.',
    keywords: [
      'istikhara dua',
      'dua for istikhara',
      'istikhara',
      'istikhara prayer',
      'salatul istikhara',
      'istikhara for marriage',
    ],
    faq: [
      {
        question: 'How do I pray istikhara for marriage, step by step?',
        answer:
          'Form the intention in your heart to ask Allah for what is best in this specific marriage decision. Pray two rak\'ah of voluntary prayer (in the first, al-Fatihah then al-Kafirun; in the second, al-Fatihah then al-Ikhlas). After the tasleem, raise your hands and recite the istikhara du\'a in full, naming the person you are asking about. Then act on what your heart settles on.',
      },
      {
        question: 'What is the full istikhara dua?',
        answer:
          'Allahumma inni astakhiruka bi \'ilmika, wa astaqdiruka bi qudratika, wa as\'aluka min fadlika al-\'azim. Fa innaka taqdiru wa la aqdir, wa ta\'lamu wa la a\'lam, wa anta \'allamu al-ghuyub. Allahumma in kunta ta\'lamu anna hadha al-amra [name it: marrying so-and-so] khayrun li fi dini wa ma\'ashi wa \'aqibati amri, faqdurhu li wa yassirhu li thumma barik li fih. Wa in kunta ta\'lamu annahu sharrun li fi dini wa ma\'ashi wa \'aqibati amri, fasrifhu \'anni wasrifni \'anhu, waqdur liya al-khayra haythu kana thumma ardini bih.',
      },
      {
        question: 'How do I know the answer to my istikhara?',
        answer:
          'Three signs taken together, not one alone: (1) your heart opening towards the decision or tightening against it, (2) the path becoming easy or blocked after you prayed, (3) your judgement settling after consulting people of knowledge and wisdom. A dream is not required and is not the main sign.',
      },
      {
        question: 'How many times should I repeat istikhara?',
        answer:
          'Scholars commonly advise three to seven times, once a day, until your heart settles on something clear. If the answer is obvious the first time, there is no need to repeat it.',
      },
      {
        question: 'Can I pray istikhara without the two rak\'ah?',
        answer:
          'The complete and correct way is the two rak\'ah followed by the du\'a. If you genuinely cannot pray — during a time when prayer is disliked, or during menstruation — you may recite the du\'a on its own, though it carries less than the full sunnah. Sleeping on it without praying is not from the sunnah.',
      },
      {
        question: 'Do I have to see a dream after istikhara?',
        answer:
          'No. A true dream is a gift Allah gives some people, not a condition. The majority of jurists hold that the real signs are the heart settling and the matter becoming easy or difficult. Waiting for a dream that never comes only delays a decision you already have the means to make.',
      },
    ],
    category: 'islamic marriage guidance',
    readTime: '7 minutes',
    publishedAt: '2026-08-24',
    lastUpdated: '2026-08-24',
    content: `
## The full istikhara dua

If you came here for the words, here they are first.

**In transliteration:**

> Allahumma inni astakhiruka bi 'ilmika, wa astaqdiruka bi qudratika, wa as'aluka min fadlika al-'azim. Fa innaka taqdiru wa la aqdir, wa ta'lamu wa la a'lam, wa anta 'allamu al-ghuyub.
>
> Allahumma in kunta ta'lamu anna hadha al-amra — **and here you name it: "marrying so-and-so"** — khayrun li fi dini wa ma'ashi wa 'aqibati amri, faqdurhu li wa yassirhu li thumma barik li fih.
>
> Wa in kunta ta'lamu annahu sharrun li fi dini wa ma'ashi wa 'aqibati amri, fasrifhu 'anni wasrifni 'anhu, waqdur liya al-khayra haythu kana thumma ardini bih.

**In English:**

> O Allah, I seek Your guidance by Your knowledge, and I seek strength by Your power, and I ask You from Your great bounty. You are able and I am not. You know and I do not. You are the Knower of the unseen.
>
> O Allah, if You know that this matter is good for me in my religion, my livelihood and the outcome of my affairs, then decree it for me, make it easy for me, and bless it for me.
>
> And if You know that it is bad for me in my religion, my livelihood and the outcome of my affairs, then turn it away from me and turn me away from it, and decree for me what is good wherever it may be, then make me content with it.

Name the person out loud where the placeholder sits. Istikhara is not a general request for a good life — it is a specific question about a specific decision.

## How to pray it, step by step

1. **Settle the intention.** You are asking Allah to choose, not asking Him to confirm what you have already decided.
2. **Pray two rak'ah** of voluntary prayer. In the first, al-Fatihah then Surat al-Kafirun. In the second, al-Fatihah then Surat al-Ikhlas. These surahs are recommended, not obligatory.
3. **After the tasleem**, raise your hands and recite the du'a above, naming the matter.
4. **Then act.** Istikhara is followed by movement, not by waiting.

There is no special time of day required, and no requirement to be facing anything other than the qiblah you already face in prayer.

## The three signs of an answer

This is where most people get stuck, so be clear about it: the answer usually arrives as **a direction, not an announcement**.

1. **Your heart opens or tightens.** After sincere istikhara, one option starts to feel settled and the other starts to feel heavy. That is the sign scholars mention most.
2. **The path eases or blocks.** Doors open — the families agree, the timing works, the practical obstacles fall away. Or they do not.
3. **Your judgement settles after consultation.** Istikhara pairs with *istisharah*: asking people of knowledge and experience. The Prophet ﷺ was commanded to consult, and consultation is part of how the answer reaches you.

**A dream is not required.** Many sincere people pray istikhara and never dream anything. That is normal, and it is not a rejection.

## How many times should you repeat it?

Three to seven times, once a day, is the common advice — until your heart settles on something clear. If the first istikhara leaves you certain, you are done. Repeating it when the answer is already clear is usually anxiety, not worship.

## What istikhara does not do

It does not replace **investigating the person**. Ask about his character, his prayer, how he treats his mother, whether he is honest in his work. Ask her family. Meet with the wali present. Istikhara asks Allah to guide a decision you have done your part to inform — it is not a substitute for doing that part.

If you want the practical checklist to go alongside it, read **[How to Pray Istikhara: Step by Step](/blog/how-to-pray-istikhara)**.

And if you are wondering how long to wait between the engagement and the wedding once your istikhara has settled, read **[How Long Should an Islamic Engagement Last?](/blog/how-long-should-an-islamic-engagement-last)**.

## After the answer

If it opens: move, with your family, and keep the process modest — the engagement is not the marriage contract, and the two of you are still non-mahram until the nikah.

If it closes: accept it as protection. "And it may be that you dislike a thing and it is good for you" (al-Baqarah 2:216).

## Looking for someone in the first place

Istikhara answers a question about a specific person. If you do not have one yet, that is a different problem — and it is the one **[Wefaqna](/register)** exists to solve: profiles reviewed before publication, no photos on public display, your family involved if you want them to be, and no subscription.

[Create a free profile](/register) or [browse profiles first](/browse) without signing up.

May Allah choose what is best for you and make you content with it.
`,
  },
  {
    slug: 'how-long-should-an-islamic-engagement-last',
    lang: 'en',
    title: 'How Long Should an Islamic Engagement Last? A Clear Answer',
    description:
      'Is there a fixed limit in the sharia? What scholars actually recommend, why long engagements go wrong, what is and is not allowed between the engaged couple, and the difference between engagement and the nikah.',
    keywords: [
      'islamic engagement duration',
      'how long should engagement last islam',
      'engagement period in islam',
      'khitbah duration',
      'engagement vs nikah',
    ],
    faq: [
      {
        question: 'Is there a fixed engagement length in Islam?',
        answer:
          'No. The sharia sets no specific limit. Contemporary scholars commonly recommend three to six months: long enough to know each other and prepare, short enough to avoid the temptation and complications that long engagements bring.',
      },
      {
        question: 'Why do scholars discourage long engagements?',
        answer:
          'Because during the engagement the two are still non-mahram to each other. The longer that period runs, the more contact accumulates, the more the boundaries erode, and the more likely a breakup becomes after both families have committed publicly. Engagements running a year or more are disliked by many jurists for exactly this reason.',
      },
      {
        question: 'What is allowed between an engaged couple?',
        answer:
          'Conversation for the purpose of getting to know each other, with the wali or a mahram present. Looking at the face and hands. Discussing values, plans and expectations. Not allowed: being alone together (khalwa), touching of any kind, repeated private messaging, or exchanging personal photos. The engagement is a promise to marry, not a marriage.',
      },
      {
        question: 'What is the difference between engagement and the marriage contract?',
        answer:
          'Engagement (khitbah) is a promise; the two remain non-mahram and either side may withdraw without divorce. The marriage contract (katb al-kitab / nikah) is a complete marriage: they are spouses in the sharia with full rights, and ending it requires a formal divorce, even if the couple have not yet moved in together.',
      },
      {
        question: 'Can we sign the nikah early and delay the wedding?',
        answer:
          'Yes, and many families do exactly that to remove the tension of a long engagement. Understand what you are agreeing to: after the contract you are married in the sharia. The rights, the responsibilities and the requirement of divorce to end it all apply from that moment, regardless of when the celebration happens.',
      },
    ],
    category: 'islamic marriage guidance',
    readTime: '6 minutes',
    publishedAt: '2026-08-24',
    lastUpdated: '2026-08-24',
    content: `
## The short answer

**The sharia sets no fixed limit.** What contemporary scholars commonly recommend is **three to six months** — long enough to know the person and prepare properly, short enough to avoid what long engagements reliably produce.

Anything past a year is disliked by many jurists, and the reason is practical rather than arbitrary.

## Why the length matters at all

During the engagement, the two of you are **still non-mahram**. Nothing has changed in the sharia. What has changed is that everyone around you now treats you as a couple — and that gap between what people assume and what is actually permitted is where the problems live.

The longer the engagement runs:

- the more private contact accumulates,
- the more the boundaries quietly move,
- the more expensive a breakup becomes, socially and emotionally, for both families.

A short engagement is not about rushing. It is about not leaving a door open longer than it needs to be.

## What is actually allowed

**Allowed:**

- Meeting and talking with the wali or a mahram present
- Looking at the face and hands
- Asking direct questions about character, religion, money, children, where you will live
- A scheduled call with family present — this is exactly why **[Wefaqna's halal call](/scheduled-call)** is audio only, time-limited and open to the wali

**Not allowed:**

- Khalwa — being alone together
- Touching of any kind
- Repeated private messaging with no purpose
- Exchanging personal photos

The Prophet ﷺ instructed a man to look at the woman he intended to marry (the hadith of al-Mughirah ibn Shu'bah). Looking with intent is the sunnah. What comes after looking has limits.

## Engagement is not the marriage contract

This is the single most common confusion, and it has real consequences.

| | Engagement (khitbah) | Marriage contract (nikah) |
|---|---|---|
| Legal status | A promise to marry | A complete marriage |
| Relationship | Still non-mahram | Lawful spouses |
| Khalwa | Forbidden | Permitted |
| Financial rights | None | Mahr and maintenance |
| Ending it | Either party withdraws | Requires formal divorce |

Some families sign the nikah early and delay the celebration, precisely to close the gap. That is a legitimate choice — but understand it fully: **from the moment of the contract you are married**, with everything that carries, even if you have not moved in together.

## Using the time well

Three to six months is enough for the things that actually matter:

1. **Meet each other's families properly.** You are marrying into a family, not just a person.
2. **Have the difficult conversations early.** Money, work after marriage, where you will live, how many children and when, how each of you behaves when angry.
3. **Verify what you were told.** Ask people who know him. Ask people who know her.
4. **Pray istikhara** — see **[Istikhara Dua: The Full Text](/blog/istikhara-dua)** for the full du'a and the real signs of an answer.

## If it is dragging on

Ask why. Usually it is one of three things: a financial obstacle nobody wants to name, a family that has not actually agreed, or one of the two hesitating and hoping the other will end it.

All three are better addressed directly than waited out. A long engagement rarely fixes a doubt; it usually just makes the eventual answer more expensive.

## Starting from the beginning

If you are not engaged yet and looking for someone serious, **[Wefaqna](/register)** reviews every profile before it is published, shows no photos publicly, and lets you involve your wali whenever you choose. It is free.

[Create your profile](/register) — or [browse first](/browse), no sign-up needed.
`,
  },
  {
    slug: 'how-to-pray-istikhara',
    lang: 'en',
    title: 'How to Pray Istikhara: Step by Step (With the Dua)',
    description:
      'The two rak\'ah, which surahs to read, when to pray it, what to say afterwards, and what to do when no clear answer comes. A short practical guide with the full dua.',
    keywords: [
      'how to pray istikhara',
      'how to perform istikhara',
      'istikhara prayer',
      'salatul istikhara',
      'istikhara namaz',
      'when to pray istikhara',
    ],
    faq: [
      {
        question: 'How many rak\'ah is istikhara?',
        answer:
          'Two rak\'ah of voluntary prayer, prayed with the intention of seeking Allah\'s choice in a specific matter. The du\'a comes after the tasleem, not inside the prayer.',
      },
      {
        question: 'Which surahs do I read in istikhara?',
        answer:
          'It is recommended to read al-Fatihah then Surat al-Kafirun in the first rak\'ah, and al-Fatihah then Surat al-Ikhlas in the second. These are recommended, not obligatory — the prayer is valid with al-Fatihah and whatever you know.',
      },
      {
        question: 'When is the best time to pray istikhara?',
        answer:
          'Any time outside the three times when prayer is disliked: at sunrise, at the sun\'s zenith, and at sunset. Many people choose the last third of the night, but no particular time is required.',
      },
      {
        question: 'Do I recite the dua before or after the prayer?',
        answer:
          'After. Pray the two rak\'ah, give the tasleem, then raise your hands and recite the du\'a, naming the matter you are asking about.',
      },
      {
        question: 'What if I get no clear answer?',
        answer:
          'Repeat it up to seven times, once a day. If nothing settles after that, act on the stronger opinion after consulting people of knowledge — istikhara is followed by action, not indefinite waiting. Allah has already answered by not making one path obviously easier; the choice is then yours to make and He has promised to bless what follows sincere istikhara.',
      },
      {
        question: 'Can a woman pray istikhara during her period?',
        answer:
          'She cannot pray the two rak\'ah, but she may recite the du\'a on its own. The full sunnah is prayer then du\'a; the du\'a alone remains permitted when prayer is not.',
      },
    ],
    category: 'islamic marriage guidance',
    readTime: '5 minutes',
    publishedAt: '2026-08-24',
    lastUpdated: '2026-08-24',
    content: `
## The short version

1. Make the intention for **one specific decision**.
2. Pray **two rak'ah** of voluntary prayer.
3. After the tasleem, **recite the du'a**, naming the matter.
4. **Act.**

That is the whole sunnah. Everything below is detail.

## Step 1 — the intention

Istikhara is not a general request for a good life. It is a question about **one thing**: this proposal, this job, this move. Decide what you are asking about before you begin, and be honest with yourself about whether you are asking Allah to choose or asking Him to approve something you have already chosen.

## Step 2 — the two rak'ah

Two rak'ah of voluntary prayer, exactly as you pray any nafl.

- **First rak'ah:** al-Fatihah, then Surat al-Kafirun
- **Second rak'ah:** al-Fatihah, then Surat al-Ikhlas

These two surahs are **recommended, not required**. If you do not know them by heart, the prayer is valid with al-Fatihah and whatever you can recite.

**When:** any time except the three times prayer is disliked — sunrise, the sun's zenith, and sunset. The last third of the night is favoured by many, but nothing obliges it.

## Step 3 — the du'a, after the tasleem

Not inside the prayer. Finish, then raise your hands.

> Allahumma inni astakhiruka bi 'ilmika, wa astaqdiruka bi qudratika, wa as'aluka min fadlika al-'azim. Fa innaka taqdiru wa la aqdir, wa ta'lamu wa la a'lam, wa anta 'allamu al-ghuyub.
>
> Allahumma in kunta ta'lamu anna hadha al-amra — **name it here** — khayrun li fi dini wa ma'ashi wa 'aqibati amri, faqdurhu li wa yassirhu li thumma barik li fih.
>
> Wa in kunta ta'lamu annahu sharrun li fi dini wa ma'ashi wa 'aqibati amri, fasrifhu 'anni wasrifni 'anhu, waqdur liya al-khayra haythu kana thumma ardini bih.

The full English meaning, line by line, is in **[Istikhara Dua: The Full Text, Transliteration and Meaning](/blog/istikhara-dua)**.

## Step 4 — then act

This is the step people skip. Istikhara is followed by **movement**. Take the next reasonable step and watch whether it eases or blocks.

## How many times?

Three to seven, once a day, until something settles. If the first one leaves you certain, stop.

## What if nothing settles?

It happens, and it is not a failure.

After seven sincere attempts with no clear inclination either way, **act on the stronger opinion** after consulting people whose judgement you trust. Allah has answered by not making one path obviously heavier — and the promise attached to istikhara is that He blesses what follows it.

Waiting indefinitely for certainty is not part of the sunnah. Deciding is.

## Common mistakes

- **Praying it inside another prayer.** It is its own two rak'ah.
- **Reciting the du'a before the tasleem.** It comes after.
- **Waiting for a dream.** Not required, and not the main sign.
- **Not naming the matter.** Say what you are asking about.
- **Praying it and then doing nothing.** Istikhara informs a decision; it does not remove the need to make one.

## For a marriage decision specifically

Do the investigating first — his prayer, his character, how he treats his mother, what her family says. Then pray. Istikhara guides a decision you have done your part to inform.

If you are still looking, **[Wefaqna](/register)** reviews every profile before publication, publishes no photos, and lets you involve your wali whenever you want. Free.

[Create a profile](/register) or [browse first](/browse).
`,
  },
  // ─────────────────────────────────────────────────────────────────────
  // GERMAN
  // ─────────────────────────────────────────────────────────────────────
  {
    slug: 'istikhara-gebet',
    lang: 'de',
    title: 'Istikhara-Gebet: das vollständige Bittgebet und die Anleitung',
    description:
      'Das vollständige Istikhara-Bittgebet in Umschrift und auf Deutsch, die Schritte des Gebets, wie oft man es wiederholt und die drei echten Zeichen einer Antwort. Ohne Anmeldung.',
    keywords: [
      'istikhara gebet',
      'istikhara heirat',
      'istikhara bittgebet',
      'salat al istikhara',
      'istikhara zeichen',
      'istikhara vor der ehe',
    ],
    faq: [
      {
        question: 'Wie bete ich Istikhara vor einer Heirat, Schritt für Schritt?',
        answer:
          'Fasse im Herzen die Absicht, Allah um das Beste in genau dieser Entscheidung zu bitten. Bete zwei Rakʿa freiwilliges Gebet (in der ersten al-Fatiha und al-Kafirun, in der zweiten al-Fatiha und al-Ichlas). Hebe nach dem Taslim die Hände und sprich das vollständige Istikhara-Bittgebet, wobei du die Person beim Namen nennst.',
      },
      {
        question: 'Wie lautet das vollständige Istikhara-Bittgebet?',
        answer:
          'Allahumma inni astachiruka bi ʿilmika, wa astaqdiruka bi qudratika, wa asʾaluka min fadlika l-ʿazim. Fa innaka taqdiru wa la aqdir, wa taʿlamu wa la aʿlam, wa anta ʿallamu l-ghuyub. Allahumma in kunta taʿlamu anna hadha l-amra [hier nennst du: die Heirat mit ...] chayrun li fi dini wa maʿaschi wa ʿaqibati amri, faqdurhu li wa yassirhu li thumma barik li fih. Wa in kunta taʿlamu annahu scharrun li fi dini wa maʿaschi wa ʿaqibati amri, fasrifhu ʿanni wasrifni ʿanhu, waqdur liya l-chayra haythu kana thumma ardini bih.',
      },
      {
        question: 'Woran erkenne ich die Antwort auf meine Istikhara?',
        answer:
          'An drei Zeichen zusammen, nicht an einem allein: (1) dein Herz öffnet sich der Entscheidung oder zieht sich zusammen, (2) der Weg wird leichter oder er versperrt sich, (3) dein Urteil festigt sich, nachdem du Menschen mit Wissen und Erfahrung gefragt hast. Ein Traum ist nicht erforderlich und ist nicht das Hauptzeichen.',
      },
      {
        question: 'Wie oft soll ich die Istikhara wiederholen?',
        answer:
          'Gelehrte empfehlen üblicherweise drei- bis siebenmal, einmal am Tag, bis dein Herz zu einer klaren Entscheidung findet. Ist die Antwort schon beim ersten Mal deutlich, brauchst du sie nicht zu wiederholen.',
      },
      {
        question: 'Darf ich Istikhara ohne die zwei Rakʿa beten?',
        answer:
          'Der vollständige und richtige Weg sind die zwei Rakʿa und danach das Bittgebet. Wenn du wirklich nicht beten kannst — etwa zu einer Zeit, in der das Gebet unerwünscht ist, oder während der Menstruation — darfst du das Bittgebet allein sprechen; es wiegt aber weniger als die vollständige Sunna. Sich ohne Gebet damit schlafen zu legen, gehört nicht zur Sunna.',
      },
      {
        question: 'Muss ich nach der Istikhara einen Traum haben?',
        answer:
          'Nein. Ein wahrer Traum ist ein Geschenk, das Allah manchen gibt, keine Bedingung. Die Mehrheit der Rechtsgelehrten hält fest, dass die eigentlichen Zeichen das Zurruhekommen des Herzens und das Leichter- oder Schwerwerden der Sache sind. Auf einen Traum zu warten, der nie kommt, verzögert nur eine Entscheidung, die du längst treffen könntest.',
      },
    ],
    category: 'islamische eheberatung',
    readTime: '7 Minuten',
    publishedAt: '2026-08-24',
    lastUpdated: '2026-08-24',
    content: `
## Das vollständige Istikhara-Bittgebet

Wenn du wegen der Worte hier bist — hier stehen sie zuerst.

**In Umschrift:**

> Allahumma inni astachiruka bi ʿilmika, wa astaqdiruka bi qudratika, wa asʾaluka min fadlika l-ʿazim. Fa innaka taqdiru wa la aqdir, wa taʿlamu wa la aʿlam, wa anta ʿallamu l-ghuyub.
>
> Allahumma in kunta taʿlamu anna hadha l-amra — **und hier nennst du es: „die Heirat mit …"** — chayrun li fi dini wa maʿaschi wa ʿaqibati amri, faqdurhu li wa yassirhu li thumma barik li fih.
>
> Wa in kunta taʿlamu annahu scharrun li fi dini wa maʿaschi wa ʿaqibati amri, fasrifhu ʿanni wasrifni ʿanhu, waqdur liya l-chayra haythu kana thumma ardini bih.

**Auf Deutsch:**

> O Allah, ich bitte Dich um Führung durch Dein Wissen, ich bitte Dich um Kraft durch Deine Macht, und ich bitte Dich um Deine große Gunst. Denn Du vermagst, und ich vermag nicht. Du weißt, und ich weiß nicht. Du bist der Kenner des Verborgenen.
>
> O Allah, wenn Du weißt, dass diese Sache gut für mich ist in meiner Religion, meinem Lebensunterhalt und dem Ausgang meiner Angelegenheiten, dann bestimme sie für mich, mache sie mir leicht und segne sie für mich.
>
> Und wenn Du weißt, dass sie schlecht für mich ist in meiner Religion, meinem Lebensunterhalt und dem Ausgang meiner Angelegenheiten, dann wende sie von mir ab und mich von ihr, und bestimme mir das Gute, wo immer es sei, und mache mich damit zufrieden.

Nenne die Person an der Stelle der Auslassung ausdrücklich. Istikhara ist keine allgemeine Bitte um ein gutes Leben — sie ist eine bestimmte Frage zu einer bestimmten Entscheidung.

## So betest du sie, Schritt für Schritt

1. **Die Absicht klären.** Du bittest Allah zu wählen — nicht darum, zu bestätigen, was du längst entschieden hast.
2. **Zwei Rakʿa beten**, freiwilliges Gebet. In der ersten al-Fatiha, dann Sure al-Kafirun. In der zweiten al-Fatiha, dann Sure al-Ichlas. Diese Suren sind empfohlen, nicht Pflicht.
3. **Nach dem Taslim** die Hände heben und das Bittgebet sprechen, wobei du die Sache benennst.
4. **Dann handeln.** Auf die Istikhara folgt Bewegung, nicht Warten.

Es gibt keine vorgeschriebene Tageszeit dafür.

## Die drei Zeichen einer Antwort

Hier bleiben die meisten hängen, deshalb deutlich gesagt: Die Antwort kommt in aller Regel **als Richtung, nicht als Ansage**.

1. **Dein Herz öffnet sich oder zieht sich zusammen.** Nach aufrichtiger Istikhara fühlt sich eine Möglichkeit ruhig an und die andere schwer. Dieses Zeichen nennen die Gelehrten am häufigsten.
2. **Der Weg wird leicht oder versperrt sich.** Türen gehen auf — die Familien stimmen zu, der Zeitpunkt passt, die praktischen Hindernisse lösen sich. Oder eben nicht.
3. **Dein Urteil festigt sich nach der Beratung.** Istikhara gehört mit *Istischara* zusammen: Menschen mit Wissen und Erfahrung zu fragen. Die Beratung ist Teil des Weges, auf dem dich die Antwort erreicht.

**Ein Traum ist nicht erforderlich.** Viele aufrichtige Menschen beten Istikhara und träumen nie etwas. Das ist normal, und es ist keine Ablehnung.

## Wie oft wiederholen?

Drei- bis siebenmal, einmal am Tag, lautet die verbreitete Empfehlung — bis dein Herz zu etwas Klarem findet. Ist die erste Istikhara eindeutig, bist du fertig. Sie zu wiederholen, obwohl die Antwort klar ist, ist meist Unruhe und nicht Gottesdienst.

## Was die Istikhara nicht leistet

Sie ersetzt **nicht das Nachfragen über den Menschen**. Frag nach seinem Charakter, nach seinem Gebet, wie er mit seiner Mutter umgeht, ob er in seiner Arbeit ehrlich ist. Frag ihre Familie. Trefft euch mit dem Wali dabei. Die Istikhara bittet Allah, eine Entscheidung zu führen, für die du deinen Teil getan hast — sie ist kein Ersatz für diesen Teil.

Die praktische Liste dazu steht hier: **[Wie lange sollte eine Verlobung im Islam dauern?](/blog/wie-lange-verlobung-im-islam)**.

Und wenn die Istikhara zur Ruhe gekommen ist und du wissen willst, wie lange die Verlobung dauern sollte: **[Wie lange sollte eine Verlobung im Islam dauern?](/blog/wie-lange-verlobung-im-islam)**.

## Nach der Antwort

Öffnet sie sich: geh den Weg, mit deiner Familie, und halte ihn zurückhaltend — die Verlobung ist nicht der Ehevertrag, und ihr seid bis zur Nikah einander weiterhin nicht Mahram.

Verschließt sie sich: nimm es als Schutz. „Doch vielleicht ist euch etwas zuwider, während es gut für euch ist" (al-Baqara 2:216).

## Und wenn es noch niemanden gibt?

Die Istikhara beantwortet eine Frage zu einer bestimmten Person. Wenn es die noch nicht gibt, ist das eine andere Aufgabe — und genau die löst **[Wefaqna](/register)**: jedes Profil wird vor der Veröffentlichung geprüft, es werden keine Fotos öffentlich gezeigt, deine Familie ist dabei, wenn du das möchtest, und es gibt kein Abo.

[Kostenloses Profil erstellen](/register) oder erst einmal [ohne Anmeldung stöbern](/browse).

Möge Allah für dich das Beste wählen und dich damit zufrieden machen.
`,
  },
  {
    slug: 'wie-lange-verlobung-im-islam',
    lang: 'de',
    title: 'Wie lange sollte eine Verlobung im Islam dauern?',
    description:
      'Gibt es eine feste Frist in der Scharia? Was Gelehrte tatsächlich empfehlen, warum lange Verlobungen schiefgehen, was zwischen Verlobten erlaubt ist — und der Unterschied zur Nikah.',
    keywords: [
      'verlobung islam dauer',
      'wie lange verlobt islam',
      'verlobungszeit islam',
      'chitba islam',
      'verlobung oder nikah',
    ],
    faq: [
      {
        question: 'Gibt es im Islam eine feste Dauer für die Verlobung?',
        answer:
          'Nein. Die Scharia setzt keine bestimmte Frist. Zeitgenössische Gelehrte empfehlen häufig drei bis sechs Monate: lang genug, um sich kennenzulernen und sich vorzubereiten, kurz genug, um die Versuchungen und Verwicklungen langer Verlobungen zu vermeiden.',
      },
      {
        question: 'Warum raten Gelehrte von langen Verlobungen ab?',
        answer:
          'Weil beide während der Verlobung einander weiterhin nicht Mahram sind. Je länger diese Zeit dauert, desto mehr Kontakt sammelt sich an, desto mehr verschieben sich die Grenzen, und desto teurer wird eine Trennung, nachdem sich beide Familien öffentlich festgelegt haben. Verlobungen von einem Jahr oder länger sehen viele Rechtsgelehrte deshalb ungern.',
      },
      {
        question: 'Was ist zwischen Verlobten erlaubt?',
        answer:
          'Ein Gespräch zum Kennenlernen in Anwesenheit des Wali oder eines Mahram. Der Blick auf Gesicht und Hände. Das Besprechen von Werten, Plänen und Erwartungen. Nicht erlaubt: Zweisamkeit (Chalwa), Berührung jeder Art, wiederholte private Nachrichten oder der Austausch privater Fotos. Die Verlobung ist ein Eheversprechen, keine Ehe.',
      },
      {
        question: 'Was unterscheidet die Verlobung vom Ehevertrag?',
        answer:
          'Die Verlobung (Chitba) ist ein Versprechen; beide bleiben einander nicht Mahram, und jede Seite kann ohne Scheidung zurücktreten. Der Ehevertrag (Katb al-Kitab / Nikah) ist eine vollständige Ehe: Sie sind nach der Scharia Eheleute mit allen Rechten, und die Auflösung erfordert eine förmliche Scheidung — auch wenn sie noch nicht zusammengezogen sind.',
      },
      {
        question: 'Können wir die Nikah früh schließen und die Feier verschieben?',
        answer:
          'Ja, und viele Familien tun genau das, um die Spannung einer langen Verlobung aufzulösen. Sei dir klar, worauf du dich einlässt: Nach dem Vertrag seid ihr nach der Scharia verheiratet. Die Rechte, die Pflichten und die Notwendigkeit einer Scheidung gelten von diesem Moment an, unabhängig davon, wann gefeiert wird.',
      },
    ],
    category: 'islamische eheberatung',
    readTime: '6 Minuten',
    publishedAt: '2026-08-24',
    lastUpdated: '2026-08-24',
    content: `
## Die kurze Antwort

**Die Scharia setzt keine feste Frist.** Was zeitgenössische Gelehrte üblicherweise empfehlen, sind **drei bis sechs Monate** — lang genug, um den Menschen kennenzulernen und sich vorzubereiten, kurz genug, um zu vermeiden, was lange Verlobungen verlässlich hervorbringen.

Alles über einem Jahr sehen viele Rechtsgelehrte ungern, und der Grund ist praktisch, nicht willkürlich.

## Warum die Dauer überhaupt zählt

Während der Verlobung seid ihr **einander weiterhin nicht Mahram**. In der Scharia hat sich nichts geändert. Geändert hat sich, dass euch alle um euch herum als Paar behandeln — und in dieser Lücke zwischen dem, was die Leute annehmen, und dem, was tatsächlich erlaubt ist, entstehen die Probleme.

Je länger die Verlobung dauert:

- desto mehr privater Kontakt sammelt sich an,
- desto stiller verschieben sich die Grenzen,
- desto teurer wird eine Trennung — gesellschaftlich und emotional, für beide Familien.

Eine kurze Verlobung bedeutet nicht Eile. Sie bedeutet, eine Tür nicht länger offen zu lassen als nötig.

## Was tatsächlich erlaubt ist

**Erlaubt:**

- Sich treffen und sprechen, mit dem Wali oder einem Mahram dabei
- Der Blick auf Gesicht und Hände
- Direkte Fragen zu Charakter, Religion, Geld, Kindern, Wohnort
- Ein vereinbartes Gespräch mit der Familie dabei — genau deshalb ist das **[Halal-Gespräch bei Wefaqna](/scheduled-call)** reiner Ton, zeitlich begrenzt und offen für den Wali

**Nicht erlaubt:**

- Chalwa — allein zu zweit sein
- Berührung jeder Art
- Wiederholte private Nachrichten ohne Anlass
- Der Austausch privater Fotos

Der Prophet ﷺ trug einem Mann auf, die Frau anzusehen, die er heiraten wollte (Hadith von al-Mughira ibn Schuʿba). Der Blick mit Absicht ist Sunna. Was danach kommt, hat Grenzen.

## Verlobung ist nicht der Ehevertrag

Das ist die häufigste Verwechslung, und sie hat echte Folgen.

| | Verlobung (Chitba) | Ehevertrag (Nikah) |
|---|---|---|
| Rechtliche Stellung | Ein Eheversprechen | Eine vollständige Ehe |
| Verhältnis | Weiterhin nicht Mahram | Rechtmäßige Eheleute |
| Chalwa | Verboten | Erlaubt |
| Vermögensrechte | Keine | Brautgabe und Unterhalt |
| Auflösung | Rücktritt einer Seite | Förmliche Scheidung nötig |

Manche Familien schließen die Nikah früh und verschieben die Feier, gerade um diese Lücke zu schließen. Das ist ein legitimer Weg — aber versteh ihn ganz: **Ab dem Vertrag seid ihr verheiratet**, mit allem, was dazugehört, auch wenn ihr noch nicht zusammengezogen seid.

## Ein Hinweis für Deutschland

Eine reine Nikah hat in Deutschland **keine rechtliche Wirkung**. Für die rechtliche Anerkennung braucht es die standesamtliche Trauung. Die meisten Paare hier heiraten zuerst standesamtlich und feiern anschließend die Nikah. Die Einzelheiten stehen in **[Muslimische Ehe in Deutschland: Standesamt und Nikah](/blog/muslimische-ehe-in-deutschland)**.

## Die Zeit gut nutzen

Drei bis sechs Monate reichen für das, worauf es ankommt:

1. **Die Familien richtig kennenlernen.** Du heiratest in eine Familie hinein, nicht nur einen Menschen.
2. **Die schwierigen Gespräche früh führen.** Geld, Arbeit nach der Heirat, Wohnort, wie viele Kinder und wann, wie sich jede und jeder von euch im Streit verhält.
3. **Nachprüfen, was man euch erzählt hat.** Fragt Leute, die ihn kennen. Fragt Leute, die sie kennen.
4. **Istikhara beten** — das vollständige Bittgebet und die echten Zeichen stehen in **[Istikhara-Gebet: Bittgebet und Anleitung](/blog/istikhara-gebet)**.

## Wenn es sich hinzieht

Frag, warum. Meist ist es eines von drei Dingen: ein finanzielles Hindernis, das niemand benennen möchte; eine Familie, die in Wahrheit nicht zugestimmt hat; oder eine Seite, die zögert und hofft, die andere möge Schluss machen.

Alle drei klärt man besser direkt, als sie auszusitzen. Eine lange Verlobung heilt einen Zweifel selten — sie macht die spätere Antwort meist nur teurer.

## Von vorn anfangen

Wenn du noch nicht verlobt bist und jemanden suchst, der es ernst meint: **[Wefaqna](/register)** prüft jedes Profil vor der Veröffentlichung, zeigt keine Fotos öffentlich und lässt dich deinen Wali einbeziehen, wann immer du möchtest. Kostenlos.

[Profil erstellen](/register) — oder erst einmal [ohne Anmeldung stöbern](/browse).
`,
  },
  // ─────────────────────────────────────────────────────────────────────
  // FRENCH
  // ─────────────────────────────────────────────────────────────────────
  {
    slug: 'douaa-istikhara',
    lang: 'fr',
    title: "Douaa Istikhara : le texte complet, la prière et les signes",
    description:
      "L'invocation complète de l'istikhara en translittération et en français, comment prier étape par étape, combien de fois la répéter, et les trois vrais signes d'une réponse. Sans inscription.",
    keywords: [
      'priere istikhara mariage',
      'salat istikhara mariage',
      'invocation istikhara',
      'douaa istikhara mariage',
      'istikhara signes',
      'comment faire istikhara',
    ],
    faq: [
      {
        question: "Comment accomplir la prière de l'istikhara pour le mariage, étape par étape ?",
        answer:
          "Formez l'intention dans votre cœur de demander à Allah ce qui est meilleur pour cette décision précise. Priez deux rakʿa surérogatoires (dans la première, al-Fatiha puis al-Kafirun ; dans la seconde, al-Fatiha puis al-Ikhlas). Après le taslim, levez les mains et récitez l'invocation complète en nommant la personne concernée.",
      },
      {
        question: "Quelle est l'invocation complète de l'istikhara ?",
        answer:
          "Allahumma inni astakhiruka bi ʿilmika, wa astaqdiruka bi qudratika, wa asʾaluka min fadlika l-ʿazim. Fa innaka taqdiru wa la aqdir, wa taʿlamu wa la aʿlam, wa anta ʿallamu l-ghuyub. Allahumma in kunta taʿlamu anna hadha l-amra [nommez : le mariage avec ...] khayrun li fi dini wa maʿashi wa ʿaqibati amri, faqdurhu li wa yassirhu li thumma barik li fih. Wa in kunta taʿlamu annahu sharrun li fi dini wa maʿashi wa ʿaqibati amri, fasrifhu ʿanni wasrifni ʿanhu, waqdur liya l-khayra haythu kana thumma ardini bih.",
      },
      {
        question: "Comment savoir la réponse à mon istikhara ?",
        answer:
          "Par trois signes réunis, jamais un seul : (1) votre cœur s'apaise face à la décision ou se resserre, (2) le chemin devient facile ou se bloque, (3) votre jugement se stabilise après avoir consulté des personnes de savoir et d'expérience. Un rêve n'est ni requis ni le signe principal.",
      },
      {
        question: "Combien de fois répéter l'istikhara ?",
        answer:
          "Les savants recommandent couramment trois à sept fois, une fois par jour, jusqu'à ce que votre cœur se fixe sur quelque chose de clair. Si la réponse est évidente dès la première fois, il n'y a pas lieu de recommencer.",
      },
      {
        question: "Peut-on faire l'istikhara sans les deux rakʿa ?",
        answer:
          "La forme complète et correcte est les deux rakʿa suivies de l'invocation. Si vous ne pouvez véritablement pas prier — pendant un moment où la prière est déconseillée, ou pendant les menstrues — vous pouvez réciter l'invocation seule, mais elle vaut moins que la sunna complète. Se coucher dessus sans prier ne relève pas de la sunna.",
      },
      {
        question: "Faut-il voir un rêve après l'istikhara ?",
        answer:
          "Non. Un rêve véridique est un don qu'Allah accorde à certains, non une condition. La majorité des juristes retiennent que les vrais signes sont l'apaisement du cœur et la facilité ou la difficulté de l'affaire. Attendre un rêve qui ne vient pas ne fait que retarder une décision que vous pouvez déjà prendre.",
      },
    ],
    category: 'mariage islamique',
    readTime: '7 minutes',
    publishedAt: '2026-08-24',
    lastUpdated: '2026-08-24',
    content: `
## L'invocation complète de l'istikhara

Si vous êtes venu pour les mots, les voici d'abord.

**En translittération :**

> Allahumma inni astakhiruka bi ʿilmika, wa astaqdiruka bi qudratika, wa asʾaluka min fadlika l-ʿazim. Fa innaka taqdiru wa la aqdir, wa taʿlamu wa la aʿlam, wa anta ʿallamu l-ghuyub.
>
> Allahumma in kunta taʿlamu anna hadha l-amra — **et ici vous le nommez : « le mariage avec … »** — khayrun li fi dini wa maʿashi wa ʿaqibati amri, faqdurhu li wa yassirhu li thumma barik li fih.
>
> Wa in kunta taʿlamu annahu sharrun li fi dini wa maʿashi wa ʿaqibati amri, fasrifhu ʿanni wasrifni ʿanhu, waqdur liya l-khayra haythu kana thumma ardini bih.

**En français :**

> Ô Allah, je Te demande de me guider par Ta science, je Te demande la force par Ta puissance, et je Te demande de Ta grâce immense. Car Tu peux et je ne peux pas. Tu sais et je ne sais pas. Tu es le Connaisseur de l'invisible.
>
> Ô Allah, si Tu sais que cette affaire est un bien pour moi dans ma religion, ma subsistance et l'issue de mes affaires, alors décrète-la pour moi, facilite-la-moi, puis bénis-la pour moi.
>
> Et si Tu sais qu'elle est un mal pour moi dans ma religion, ma subsistance et l'issue de mes affaires, alors détourne-la de moi et détourne-moi d'elle, et décrète pour moi le bien où qu'il soit, puis fais que j'en sois satisfait.

Nommez la personne à l'endroit indiqué. L'istikhara n'est pas une demande générale de bonne vie : c'est une question précise sur une décision précise.

## Comment l'accomplir, étape par étape

1. **Fixez l'intention.** Vous demandez à Allah de choisir, pas de confirmer ce que vous avez déjà décidé.
2. **Priez deux rakʿa** surérogatoires. Dans la première, al-Fatiha puis sourate al-Kafirun. Dans la seconde, al-Fatiha puis sourate al-Ikhlas. Ces sourates sont recommandées, non obligatoires.
3. **Après le taslim**, levez les mains et récitez l'invocation en nommant l'affaire.
4. **Puis agissez.** L'istikhara est suivie de mouvement, non d'attente.

Aucun moment particulier de la journée n'est requis.

## Les trois signes d'une réponse

C'est ici que la plupart des gens se bloquent, alors soyons clairs : la réponse arrive presque toujours **comme une direction, pas comme une annonce**.

1. **Votre cœur s'apaise ou se resserre.** Après une istikhara sincère, une option devient paisible et l'autre devient lourde. C'est le signe que les savants mentionnent le plus.
2. **Le chemin s'ouvre ou se bloque.** Les portes s'ouvrent — les familles s'accordent, le moment convient, les obstacles pratiques tombent. Ou non.
3. **Votre jugement se stabilise après consultation.** L'istikhara va de pair avec l'*istishara* : consulter des gens de savoir et d'expérience. La consultation fait partie du chemin par lequel la réponse vous parvient.

**Un rêve n'est pas requis.** Beaucoup de gens sincères font l'istikhara et ne rêvent jamais rien. C'est normal, et ce n'est pas un refus.

## Combien de fois la répéter ?

Trois à sept fois, une fois par jour, est le conseil courant — jusqu'à ce que votre cœur se fixe sur quelque chose de clair. Si la première istikhara vous laisse certain, c'est terminé. La répéter alors que la réponse est claire relève le plus souvent de l'inquiétude, non de l'adoration.

## Ce que l'istikhara ne fait pas

Elle ne remplace **pas le fait de se renseigner sur la personne**. Renseignez-vous sur son caractère, sa prière, la façon dont il traite sa mère, son honnêteté au travail. Interrogez sa famille. Rencontrez-vous avec le wali présent. L'istikhara demande à Allah de guider une décision que vous avez pris la peine d'éclairer — elle ne remplace pas cette peine.

La liste pratique se trouve ici : **[Combien de temps doivent durer les fiançailles en islam ?](/blog/duree-fiancailles-islam)**.

Et lorsque l'istikhara s'est apaisée, pour savoir combien de temps doivent durer les fiançailles : **[Combien de temps doivent durer les fiançailles en islam ?](/blog/duree-fiancailles-islam)**.

## Après la réponse

Si elle s'ouvre : avancez, avec votre famille, en gardant la retenue — les fiançailles ne sont pas le contrat de mariage, et vous restez non-mahram jusqu'au nikah.

Si elle se ferme : recevez-le comme une protection. « Or, il se peut que vous ayez de l'aversion pour une chose alors qu'elle vous est un bien » (al-Baqara 2:216).

## Et s'il n'y a encore personne ?

L'istikhara répond à une question sur une personne précise. S'il n'y en a pas encore, c'est une autre tâche — et c'est celle que **[Wefaqna](/register)** résout : chaque profil est vérifié avant publication, aucune photo n'est affichée publiquement, votre famille participe si vous le souhaitez, et il n'y a aucun abonnement.

[Créer un profil gratuit](/register) ou [parcourir d'abord](/browse), sans inscription.

Qu'Allah choisisse pour vous ce qui est meilleur et vous en rende satisfait.
`,
  },
  {
    slug: 'duree-fiancailles-islam',
    lang: 'fr',
    title: 'Combien de temps doivent durer les fiançailles en islam ?',
    description:
      "Existe-t-il une durée fixée par la charia ? Ce que les savants recommandent réellement, pourquoi les longues fiançailles tournent mal, ce qui est permis entre fiancés, et la différence avec le nikah.",
    keywords: [
      'duree fiancailles islam',
      'combien de temps fiancailles islam',
      'khitba islam',
      'fiancailles ou nikah',
      'periode de fiancailles musulmane',
    ],
    faq: [
      {
        question: 'Existe-t-il une durée fixe des fiançailles en islam ?',
        answer:
          "Non. La charia ne fixe aucune durée. Les savants contemporains recommandent couramment trois à six mois : assez long pour se connaître et se préparer, assez court pour éviter les tentations et les complications qu'entraînent les longues fiançailles.",
      },
      {
        question: 'Pourquoi les savants déconseillent-ils les longues fiançailles ?',
        answer:
          "Parce que pendant les fiançailles, les deux restent non-mahram l'un pour l'autre. Plus cette période dure, plus les contacts s'accumulent, plus les limites s'effacent, et plus une rupture devient coûteuse une fois que les deux familles se sont engagées publiquement. Des fiançailles d'un an ou plus sont désapprouvées par beaucoup de juristes pour cette raison.",
      },
      {
        question: 'Que peut-on faire entre fiancés ?',
        answer:
          "Se parler pour se connaître, en présence du wali ou d'un mahram. Regarder le visage et les mains. Discuter des valeurs, des projets et des attentes. Ne sont pas permis : se retrouver seuls (khalwa), tout contact physique, des messages privés répétés, ou l'échange de photos personnelles. Les fiançailles sont une promesse de mariage, pas un mariage.",
      },
      {
        question: 'Quelle différence entre les fiançailles et le contrat de mariage ?',
        answer:
          "Les fiançailles (khitba) sont une promesse ; les deux restent non-mahram et chacun peut se retirer sans divorce. Le contrat de mariage (nikah) est un mariage complet : ils sont époux selon la charia avec tous les droits, et y mettre fin exige un divorce en bonne et due forme, même sans vie commune.",
      },
      {
        question: 'Peut-on conclure le nikah tôt et repousser la fête ?',
        answer:
          "Oui, et beaucoup de familles font exactement cela pour dissiper la tension de longues fiançailles. Sachez ce que vous engagez : après le contrat, vous êtes mariés selon la charia. Les droits, les devoirs et la nécessité d'un divorce pour y mettre fin s'appliquent dès cet instant, quelle que soit la date de la fête.",
      },
    ],
    category: 'mariage islamique',
    readTime: '6 minutes',
    publishedAt: '2026-08-24',
    lastUpdated: '2026-08-24',
    content: `
## La réponse courte

**La charia ne fixe aucune durée.** Ce que les savants contemporains recommandent couramment, ce sont **trois à six mois** — assez long pour connaître la personne et se préparer correctement, assez court pour éviter ce que les longues fiançailles produisent immanquablement.

Au-delà d'un an, beaucoup de juristes désapprouvent, et la raison est pratique, non arbitraire.

## Pourquoi la durée compte

Pendant les fiançailles, vous restez **non-mahram l'un pour l'autre**. Rien n'a changé selon la charia. Ce qui a changé, c'est que tout le monde autour de vous vous traite désormais comme un couple — et c'est précisément dans cet écart, entre ce que les gens supposent et ce qui est réellement permis, que naissent les problèmes.

Plus les fiançailles durent :

- plus les contacts privés s'accumulent,
- plus les limites se déplacent en silence,
- plus une rupture devient coûteuse, socialement et affectivement, pour les deux familles.

Des fiançailles courtes ne signifient pas se précipiter. Elles signifient ne pas laisser une porte ouverte plus longtemps que nécessaire.

## Ce qui est réellement permis

**Permis :**

- Se rencontrer et se parler, avec le wali ou un mahram présent
- Regarder le visage et les mains
- Poser des questions directes : caractère, religion, argent, enfants, lieu de vie
- Un appel programmé avec la famille présente — c'est exactement pourquoi l'**[appel halal de Wefaqna](/scheduled-call)** est audio uniquement, limité dans le temps et ouvert au wali

**Non permis :**

- La khalwa — se retrouver seuls
- Tout contact physique
- Des messages privés répétés sans objet
- L'échange de photos personnelles

Le Prophète ﷺ a ordonné à un homme de regarder la femme qu'il voulait épouser (hadith d'al-Mughira ibn Shuʿba). Le regard avec intention relève de la sunna. Ce qui vient après a des limites.

## Les fiançailles ne sont pas le contrat

C'est la confusion la plus répandue, et elle a des conséquences réelles.

| | Fiançailles (khitba) | Contrat de mariage (nikah) |
|---|---|---|
| Statut | Une promesse de mariage | Un mariage complet |
| Relation | Toujours non-mahram | Époux légitimes |
| Khalwa | Interdite | Permise |
| Droits financiers | Aucun | Mahr et entretien |
| Y mettre fin | Retrait d'une partie | Divorce en bonne et due forme |

Certaines familles concluent le nikah tôt et repoussent la fête, précisément pour combler cet écart. C'est un choix légitime — mais comprenez-le entièrement : **dès le contrat, vous êtes mariés**, avec tout ce que cela emporte, même sans vie commune.

## Une précision pour la France

Un nikah seul n'a **aucun effet juridique** en France. La reconnaissance légale passe par le mariage civil à la mairie. La plupart des couples se marient civilement d'abord, puis célèbrent le nikah. Prévoyez ce délai dans votre calendrier : c'est souvent lui qui allonge les fiançailles, pas le désir d'attendre.

## Bien utiliser ce temps

Trois à six mois suffisent pour ce qui compte vraiment :

1. **Rencontrer les familles pour de bon.** Vous entrez dans une famille, pas seulement auprès d'une personne.
2. **Avoir tôt les conversations difficiles.** L'argent, le travail après le mariage, le lieu de vie, combien d'enfants et quand, la façon dont chacun se comporte en colère.
3. **Vérifier ce qu'on vous a dit.** Interrogez ceux qui le connaissent. Interrogez ceux qui la connaissent.
4. **Faire l'istikhara** — l'invocation complète et les vrais signes sont ici : **[Douaa Istikhara : le texte complet](/blog/douaa-istikhara)**.

## Si cela traîne

Demandez pourquoi. C'est en général l'une de trois choses : un obstacle financier que personne ne veut nommer, une famille qui n'a pas réellement donné son accord, ou l'un des deux qui hésite en espérant que l'autre mette fin.

Ces trois-là se règlent mieux directement qu'en attendant. De longues fiançailles guérissent rarement un doute ; le plus souvent, elles rendent seulement la réponse finale plus coûteuse.

## Repartir du début

Si vous n'êtes pas fiancé et cherchez quelqu'un de sérieux, **[Wefaqna](/register)** vérifie chaque profil avant publication, n'affiche aucune photo, et vous laisse impliquer votre wali quand vous le souhaitez. C'est gratuit.

[Créer votre profil](/register) — ou [parcourir d'abord](/browse), sans inscription.
`,
  },
];
